import { ChangeEvent } from 'react';
import { Link, TextField } from '@mui/material';
import { brightpathTheme } from '@youscience/theme';
import { CoreFormControl, CoreTypography } from '@youscience/core';
import { YesNo, FeeWaiverRequest, FeeWaiverSchema } from '@youscience/commonapp-service-common';
import { FormState } from '@constants/commonApp/types';
import SectionAccordion from '@components/SectionAccordion';
import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type EconomicNeedFields = Partial<FeeWaiverRequest['economicNeed']>;

const FEE_WAIVER_INFO_LINK =
  'https://recsupport.commonapp.org/recommendersupport/s/article/What-do-I-need-to-know-about-the-Common-App-fee-waiver';

interface EconomicNeedProps {
  economicNeed?: EconomicNeedFields;
  onFormChange: (formState: FormState<EconomicNeedFields>) => void;
  onExpand: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const validator = FeeWaiverSchema.shape.economicNeed;

export const EconomicNeed = ({ economicNeed = {}, onFormChange, onExpand, isExtended, isSaved }: EconomicNeedProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<EconomicNeedFields, typeof validator>(
    economicNeed,
    validator,
    isSaved,
    onFormChange,
  );

  const handleTextFieldChange =
    ({ optional = false } = {}) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setFormStateWithErrors({ [event.target.name]: event.target.value || (optional ? null : undefined) });
    };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleRadioChange = (fieldName: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [event.target.name]: event.target.value || undefined });
  };

  return (
    <SectionAccordion
      title="Economic Need"
      onChange={onExpand}
      isExpanded={isExtended}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreTypography sx={{ color: brightpathTheme.palette.grey['500'] }} variant="body1">
          This student indicated that they believe they meet the eligibility requirements for a{' '}
          <Link href={FEE_WAIVER_INFO_LINK} target="_blank">
            Common App fee waiver
          </Link>
          .
        </CoreTypography>

        <RadioButtonGroupWithClearButton
          name="question"
          required={true}
          title="To the best of your knowledge, do you believe the student is eligible for a Common App fee waiver?"
          value={formState.data.question ?? undefined}
          options={YesNo}
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ question: undefined })}
          error={!!formState.errors.question}
          helperText={formState.errors.question}
        />

        <CoreTypography sx={{ color: brightpathTheme.palette.grey['500'] }} variant="body1">
          You may use this space to provide any additional information in support of this student’s fee waiver
          eligibility.
        </CoreTypography>

        <CoreFormControl fullWidth>
          <TextField
            name="additionalInformation"
            label="Student’s fee waiver eligibility"
            value={formState.data.additionalInformation ?? ''}
            error={!!formState.errors.additionalInformation}
            helperText={formState.errors.additionalInformation}
            onChange={handleTextFieldChange({ optional: true })}
            multiline
          />
        </CoreFormControl>
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
